import * as Yup from 'yup';
import { BaseSchema } from 'yup';
import { FormikErrors, yupToFormErrors } from 'formik';
import Workflow from '../../../models/be/Workflow';
import Document from '../../../models/be/db/Document';
import {
	hasValue,
	nestedArrayReducer,
	getNestedFieldValue,
	singleOrUndefined,
	getAllInstancesOfRegexCapturingGroup,
	isClassObject,
	distinct,
	subtract,
	getLocaleDate,
	deepCopy,
	setNestedFieldValue,
	getFormattedDate,
	validateMedicareNumber
} from '../../../lib/utils';
import FormSpecification, {
	FormSpecificationPage,
	Component,
	Section,
	AstExpression,
	ComponentProps
} from '../../../models/be/db/FormSpecification';
import { evalString, evalAst, evalBoolean, evalNumber } from '../../../core/ast';
import WorkflowTemplate from '../../../models/be/WorkflowTemplate';
import { isAstExpression } from '../../../core/astInterpreter';
import { isCompletedDocument } from '../../../core/domain';
import Option from '../../../models/Option';
import { validateAddressHistoryEditor } from '../../../core/addressHistories';
import { validateAdditionalNamesEditor } from '../../../core/additionalNames';
import { partialUpdateWorkflow } from '../../../core/api';
import { uninterruptedYearsOfAddressHistories } from '../../../config.local';

// Types aren't clean for deep-equal
// eslint-disable-next-line @typescript-eslint/no-var-requires
const deepEqual = require('deep-equal');

export const formErrorKey = 'formError';
export const hiddenErrorKey = 'hiddenError';
export const requiredErrorMessage = 'Required.';
let requiredFieldErrorMessage = 'Required.';

export const getFormSpecificationPage = (formSpecification: FormSpecification, pageSlug: string) => {
	const formSpecificationPage = formSpecification.pages.find((f) => f.slug === pageSlug);
	if (formSpecificationPage === undefined) {
		return null;
	}
	return formSpecificationPage;
};

export const getInitializationSections = (workflowTemplates: WorkflowTemplate[]) => {
	const initSections: Section[] = [];
	workflowTemplates.forEach((w) => {
		if (w.initialisationSection !== null) {
			initSections.push(w.initialisationSection);
		}
	});
	return initSections || null;
};

export const getInitialValuesForPage = (formSpecificationPage: FormSpecificationPage, workflow: Workflow) => {
	return getInitialValuesForSections(formSpecificationPage.sections, workflow);
};

export const getInitialValuesForSections = <T>(section: Section[], source: T = null) => {
	const compoentsWithValidFieldNames = section
		.map((s) => s.components)
		.reduce(nestedArrayReducer, [])
		.filter((c) => c.fieldNames.length >= 1);
	return getInitialValuesForComponents(source, compoentsWithValidFieldNames);
};

const getInitialValuesForComponents = <T>(source: T, components: Component[]) => {
	const fieldNameAndTypes = components
		.map((c) => {
			const type = c.fieldNames.length === 1 ? c.props.type : undefined;
			return c.fieldNames.map((n) => [n, type]);
		})
		.reduce(nestedArrayReducer, []);

	const initialValues = {} as T;
	fieldNameAndTypes.forEach((x) => {
		const [fieldName, type] = x;
		let srcObject: any = source;
		let destObject: any = initialValues;
		const evaluatedFieldName = isAstExpression(fieldName)
			? evalString(type as AstExpression, initialValues)
			: (fieldName as string);
		const parts = evaluatedFieldName.split('.');
		for (let i = 0; i < parts.length; i++) {
			const part = parts[i];
			const value = !hasValue(srcObject) ? null : srcObject[part];
			if (i === parts.length - 1) {
				destObject[part] = (value === undefined || value === null) && type === 'text' ? '' : value ?? null;
				// eslint-disable-next-line no-prototype-builtins
			} else if (!destObject.hasOwnProperty(part)) {
				destObject[part] = {};
			}
			destObject = destObject[part];
			srcObject = value;
		}
	});

	return initialValues;
};

export const getFieldNamesForSection = (section: Section) => {
	return section.components
		.filter((c) => c.fieldNames.length > 0)
		.map((c) => c.fieldNames)
		.reduce(nestedArrayReducer, []);
};

export const validateFormSpecificationPageSections =
	(sections: Section[]) =>
	(values: unknown): FormikErrors<unknown> => {
		return validate(getVisibleComponents(sections, values))(values);
	};

export const validate =
	(components: Component[]) =>
	(values: unknown): FormikErrors<unknown> => {
		const schema = getSchema(components, values);
		try {
			schema.validateSync(values, { abortEarly: false });
			return null;
		} catch (e) {
			if (e instanceof Yup.ValidationError) {
				return yupToFormErrors(e);
			}
			return { [formErrorKey]: 'Unable to validate this page. Please review your details and try again.' };
		}
	};

export const getInvisibleFields = (formSpecificationSections: Section[], values: unknown) => {
	let visibleFieldNames: string[] = [];
	let invisibleFieldNames: string[] = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const section of formSpecificationSections) {
		if (evalBoolean(section.visible, values)) {
			// eslint-disable-next-line no-restricted-syntax
			for (const component of section.components) {
				if (evalBoolean(component.visible, values)) {
					visibleFieldNames = visibleFieldNames.concat(component.fieldNames);
				} else {
					invisibleFieldNames = invisibleFieldNames.concat(component.fieldNames);
				}
			}
		} else {
			// eslint-disable-next-line no-restricted-syntax
			for (const component of section.components) {
				invisibleFieldNames = invisibleFieldNames.concat(component.fieldNames);
			}
		}
	}
	// A field may be visible in one section and invisible in another.
	return distinct(subtract(invisibleFieldNames, visibleFieldNames));
};

// TODO: This fn name is incorrect as it doesn't filter out the invisible Components but Sections only.
// We need to find out whether it is only the fn name issue or it is a bug.
const getVisibleComponents = (sections: Section[], values: unknown) => {
	return sections
		.filter((s) => evalBoolean(s.visible, values))
		.map((s) => s.components)
		.reduce(nestedArrayReducer, []);
};

export const getSchema = (
	components: Component[],
	allValues: unknown,
	currentValues?: any,
	partialFieldName?: string
) => {
	const schemaFields = {} as any;
	if (currentValues === undefined) {
		// eslint-disable-next-line no-param-reassign
		currentValues = allValues;
	}

	let schema = Yup.object();

	const prefix = partialFieldName === undefined ? '' : `${partialFieldName}.`;
	// eslint-disable-next-line no-restricted-syntax, guard-for-in
	for (const key in currentValues) {
		const value = currentValues[key];
		const fieldName = prefix + key;
		const formSpecComponents = components.filter((c) => c.fieldNames[0] === fieldName);
		const component =
			formSpecComponents.length > 1
				? formSpecComponents.find((c) => evalBoolean(c.visible, allValues))
				: formSpecComponents[0];
		// eslint-disable-next-line no-prototype-builtins
		if (component !== undefined && component.props.hasOwnProperty('type')) {
			if (evalBoolean(component.visible, allValues)) {
				schemaFields[key] = createFieldValidationSchema(component, allValues);
			}
		} else if (component !== undefined && component.name === 'Statement') {
			if (evalBoolean(component.visible, allValues)) {
				schemaFields[key] = Yup.boolean()
					.nullable()
					.test('statement', 'To proceed, please agree to the statement above.', (thisValue: boolean | null) => {
						return thisValue === true;
					});
			}
		} else if (isClassObject(value)) {
			schemaFields[key] = getSchema(components, allValues, value, prefix + key);
		}
	}

	// If this is the top level schema object.
	if (partialFieldName === undefined) {
		schema = addFormLevelValidation(schema, components);
	}
	return schema.shape(schemaFields);
};

const addFormLevelValidation = (schema: Yup.ObjectSchema<any>, components: Component[]) => {
	// eslint-disable-next-line no-param-reassign
	schema = addFormValidation(schema, components);
	// eslint-disable-next-line no-param-reassign
	schema = addAdditionalNamesValidation(schema, components);
	// eslint-disable-next-line no-param-reassign
	schema = addAddressHistoryValidation(schema, components);
	return schema;
};

const addFormValidation = (schema: Yup.ObjectSchema<any>, components: Component[]) => {
	const formValidationComponent = singleOrUndefined(components.filter((c) => c.name === 'FormValidation'));

	if (formValidationComponent !== undefined) {
		// eslint-disable-next-line no-param-reassign
		schema = schema.test('validateForm', null, function validateFormTestFn(values) {
			const isValid = evalBoolean(formValidationComponent.props.isValid, values);
			const { errorMessage } = formValidationComponent.props;

			if (isValid) {
				return true;
			}
			return this.createError({ path: formErrorKey, message: errorMessage });
		});
	}

	return schema;
};

const addAdditionalNamesValidation = (schema: Yup.ObjectSchema<any>, components: Component[]) => {
	const component = singleOrUndefined(components.filter((c) => c.name === 'AdditionalNamesEditor'));
	let updatedSchema = schema;
	if (component !== undefined) {
		updatedSchema = schema.test('validateAdditionalNamesEditor', null, function validateAdditionalNames(values) {
			const additionalNames = getNestedFieldValue(values, component.fieldNames[0]);
			const message = validateAdditionalNamesEditor(additionalNames, component.props);

			if (!hasValue(message)) {
				return true;
			}
			return this.createError({ path: component.fieldNames[0], message });
		});
	}

	return updatedSchema;
};

const addAddressHistoryValidation = (schema: Yup.ObjectSchema<any>, components: Component[]) => {
	const component = singleOrUndefined(components.filter((c) => c.name === 'AddressHistoryEditor'));
	let updatedSchema = schema;
	if (component !== undefined) {
		updatedSchema = schema.test('validateAddressHistoryEditor', null, function validateAddressHistories(values) {
			const addressHistories = getNestedFieldValue(values, component.fieldNames[0]);
			const message = validateAddressHistoryEditor(
				addressHistories,
				component.props.numberOfYearsToCover ?? uninterruptedYearsOfAddressHistories,
				component.props.countryRestriction
			);

			if (!hasValue(message)) {
				return true;
			}
			return this.createError({ path: component.fieldNames[0], message });
		});
	}

	return updatedSchema;
};

const createFieldValidationSchema = (component: Component, values: unknown) => {
	const fieldSchema = createFieldSchema(evalString(component.props.type, values));
	return addValidationRules(fieldSchema, values, component.props);
};

const createFieldSchema = (type: string) => {
	switch (type) {
		case 'text':
		case 'phoneNumber':
		case 'staticText':
		case 'richText':
		case 'comboBox':
		case 'dropDownList':
		case 'date':
		case 'monthYear':
			return Yup.string().nullable();
		case 'checkbox':
		case 'boolean':
			return Yup.boolean().nullable();
		case 'number':
			return Yup.number();
		case 'file':
		case 'line':
		case 'document':
		case 'signature':
		case 'addressEditor':
		case 'documentDownload':
			return Yup.object().nullable();
		case 'checkList':
			return Yup.array().nullable();
		default:
			throw new Error(`Unknown type: ${type}.`);
	}
};

const addValidationRules = <T>(schema: any, values: unknown, componentProps: ComponentProps): BaseSchema<T> => {
	const nonValidationProps = ['type', 'label', 'hidden', 'disabled', 'patternErrorMessage'];
	const type = evalString(componentProps.type, values);
	const hidden = hasValue(componentProps.hidden) ? evalBoolean(componentProps.hidden, values) : false;

	if (!hidden) {
		// eslint-disable-next-line no-restricted-syntax
		for (const key in componentProps) {
			if (nonValidationProps.indexOf(key) !== -1) {
				// eslint-disable-next-line no-continue
				continue;
			}
			if (type === 'text' && key === 'values') {
				// eslint-disable-next-line no-continue
				continue;
			}
			const value = componentProps[key];
			const errorMessage = componentProps[`${key}ErrorMessage`] as string | AstExpression; // This may be undefined
			const evaluatedErrorMessage = isAstExpression(errorMessage)
				? evalString(errorMessage as AstExpression, values)
				: (errorMessage as string);
			// eslint-disable-next-line no-param-reassign
			schema =
				isAstExpression(value) && key !== 'values'
					? addAstValidateRule(schema, values, key, value as AstExpression, evaluatedErrorMessage)
					: addValidationRule(
							schema,
							key,
							value,
							evaluatedErrorMessage,
							componentProps.required,
							componentProps.label as string
					  );
		}
	}

	if (componentProps.type === 'file' && evalBoolean(componentProps.required, values)) {
		// eslint-disable-next-line no-param-reassign
		schema = schema.test('documentFieldRequired', requiredErrorMessage, (value: Document) => {
			return isCompletedDocument(value);
		});
	}

	return schema;
};

const addAstValidateRule = (
	schema: any,
	values: unknown,
	name: string,
	ast: number | string | AstExpression,
	errorMessage: string
) => {
	switch (name) {
		case 'required':
			return schema.test('required', errorMessage || 'Required.', (value: unknown) => {
				const required = evalAst(ast, values, null);
				return !required || hasValue(value);
			});
		case 'pattern':
			return schema.test('pattern', errorMessage || 'Invalid input.', (value: string) => {
				const pattern = evalString(ast as string | AstExpression, values);
				return pattern === null || new RegExp(pattern).test(value);
			});
		case 'min':
			return schema.test('min', errorMessage || 'This is less than the minimum.', (value: string | Date) => {
				if (!hasValue(value)) {
					return true;
				}
				const min = evalAst(ast, values, null);
				return min === null || parseStringAsDateAndPassThroughOtherTypes(value) >= min;
			});
		case 'max':
			return schema.test('max', errorMessage || 'This is greater than the maximum.', (value: string | Date) => {
				if (!hasValue(value)) {
					return true;
				}
				const max = evalAst(ast, values, null);
				return max === null || parseStringAsDateAndPassThroughOtherTypes(value) <= max;
			});
		case 'minLength':
			return schema.test('minLength', errorMessage || 'This is shorter than the minimum length.', (value: string) => {
				const minLength = evalNumber(ast as number | AstExpression, values);
				return minLength === null || value.length >= minLength;
			});
		case 'maxLength':
			return schema.test('maxLength', errorMessage || 'This is longer than the maximum length.', (value: string) => {
				const maxLength = evalNumber(ast as number | AstExpression, values);
				return maxLength === null || value.length <= maxLength;
			});
		case 'validationType':
			return schema.test('validationType', errorMessage || 'Invalid Value.', (value: string) => {
				const validationType = evalString(ast as string | AstExpression, values);
				if (validationType === 'medicareNumber') return validateMedicareNumber(value);
				return null;
			});

		default:
			throw new Error(`Unknown validation rule name: ${name}.`);
	}
};

const parseStringAsDateAndPassThroughOtherTypes = (value: string | Date) => {
	if (typeof value === 'string') {
		return getLocaleDate(value);
	}
	return value;
};

const addValidationRule = (
	schema: any,
	name: string,
	value: any,
	errorMessage: string,
	isRequired: boolean,
	field: string
) => {
	switch (name) {
		case 'required': {
			if (typeof field === 'string' && field?.toLowerCase() === 'street name') {
				requiredFieldErrorMessage = 'No Street Name? Put other addressing details here instead.';
			} else {
				requiredFieldErrorMessage = 'Required.';
			}
			return value === true ? schema.required(errorMessage || requiredFieldErrorMessage).nullable() : schema.optional();
		}
		case 'values': {
			const convertedValues = convertOptionsToValues(value);
			// Skip when the values list is empty — options may be populated at runtime from check
			// variables, so an empty list at validation time should not reject all selections.
			if (!Array.isArray(convertedValues) || convertedValues.length === 0) return schema;
			const invalidOptionErrorMessage = 'Please select a valid option.';
			return schema.test('values', errorMessage || invalidOptionErrorMessage, (fieldValue: string) => {
				if (!hasValue(fieldValue)) return true;
				const candidate = typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue;
				return convertedValues.some((v) => (typeof v === 'string' ? v.trim() : v) === candidate);
			});
		}
		case 'minLength':
			// Because the proper min rule doesn't work correctly with optional fields, we need to implement a custom rule instead.
			return schema.test(
				'minLength',
				errorMessage || 'This is shorter than the minimum length.',
				(fieldValue: string) => {
					const minLength = value;
					return !hasValue(fieldValue) || fieldValue.length >= minLength;
				}
			);
		case 'maxLength':
			return schema.max(value, `Must be ${value} characters or less`);
		case 'pattern':
			return schema.matches(value, { message: errorMessage, excludeEmptyString: true });
		case 'min':
			return schema.min(value, errorMessage || `Must be equal to or greater than ${value}.`);
		case 'max':
			return schema.max(value, errorMessage || `Must be equal to or less than ${value}.`);
		case 'validationType':
			if (value === 'medicareNumber')
				return schema.test(
					'validationType',
					errorMessage || 'Medicare card number is incorrect',
					(fieldValue: string) => {
						return validateMedicareNumber(fieldValue);
					}
				);
			return null;
		default:
			// TODO: Should I throw an error here and force us to ignore all known non validation props?
			// throw new Error(`Unknown name: ${name}.`);
			return schema;
	}
};

// This is based on options format: [{ value: 'AUS', text: 'Australia' }].
const convertOptionsToValues = (options: Option<string>[] | string[]) => {
	if (options.length > 0 && hasValue((options[0] as Option<string>).value)) {
		return (options as Option<string>[]).map((x) => x.value);
	}
	return options;
};

export const populateTemplateIfValuesChanged = <T>(template: string, previousValues: T, values: T) => {
	const templateFieldNames = getTemplateFieldNames(template);
	const previousTemplateFieldValues = templateFieldNames.map((n) => getNestedFieldValue(previousValues, n));
	const currentTemplateFieldValues = templateFieldNames.map((n) => getNestedFieldValue(values, n));
	if (currentTemplateFieldValues.filter((t: unknown) => !hasValue(t)).length > 0) return null;
	if (deepEqual(previousTemplateFieldValues, currentTemplateFieldValues)) {
		return null;
	}
	const fieldNamesToValues = templateFieldNames.reduce(
		(dict: Record<string, string>, fieldName: string, index: number) => {
			// eslint-disable-next-line no-param-reassign
			dict[fieldName] = currentTemplateFieldValues[index];
			return dict;
		},
		{}
	);
	return populateTemplate(template, fieldNamesToValues);
};

export const populateTemplateUsingObject = (template: string, object: unknown) => {
	const templateFieldNames = getTemplateFieldNames(template);
	const templateFieldValues = templateFieldNames.map((n) => getNestedFieldValue(object, n));
	const fieldNamesToValues = templateFieldNames.reduce(
		(dict: Record<string, string>, fieldName: string, index: number) => {
			// eslint-disable-next-line no-param-reassign
			dict[fieldName] = templateFieldValues[index];
			return dict;
		},
		{}
	);
	return populateTemplate(template, fieldNamesToValues);
};

const getTemplateFieldNames = (template: string) => {
	const templateValueRegex = /\{([\w.]+)\}/gm;
	const matchIndex = 1;
	const matches = getAllInstancesOfRegexCapturingGroup(templateValueRegex, template, matchIndex);
	return Array.from(new Set(matches));
};

const populateTemplate = (template: string, fieldNamesToValues: Record<string, string>) => {
	// eslint-disable-next-line no-restricted-syntax, guard-for-in
	for (const fieldName in fieldNamesToValues) {
		const regex = new RegExp(`{${fieldName}}`, 'g');
		// eslint-disable-next-line no-param-reassign
		template = template.replace(regex, fieldNamesToValues[fieldName]);
	}
	return template;
};

export const filterOutFieldsThatAreInvisibleInPage = (
	workflow: Workflow,
	values: unknown,
	formSpecificationPage: FormSpecificationPage
) => {
	const updatedWorkflow = { ...deepCopy(workflow), ...deepCopy(values) };
	const invisibleFieldNames = getInvisibleFields(formSpecificationPage.sections, updatedWorkflow);
	invisibleFieldNames.forEach((fieldName) => {
		setNestedFieldValue(updatedWorkflow, fieldName, null);
	});

	return updatedWorkflow;
};

export const saveWorkflowData = async (
	allValues: Workflow,
	newValues: unknown,
	activityId: string,
	formSpecificationPage: FormSpecificationPage,
	removeInvisibleFields = false
) => {
	const partialWorkflow = getPartialWorkflow(allValues.clientCustomerId, newValues);
	if (removeInvisibleFields) {
		// When updating the workflow, we want to set all of the invisible fields to null
		const fieldNames = getInvisibleFields(formSpecificationPage.sections, newValues);
		fieldNames.forEach((nestedFieldName) => {
			setNestedFieldValue(partialWorkflow, nestedFieldName, null);
		});
		if (fieldNames.find((n) => n === 'additionalNames')) {
			// Additional Names field can never be null.
			setNestedFieldValue(partialWorkflow, 'additionalNames', []);
		}
	}

	return await partialUpdateWorkflow(allValues.id, activityId, partialWorkflow);
};

const getPartialWorkflow = (clientCustomerId: number, newValues: unknown): Record<string, unknown> => {
	return { clientCustomerId, ...deepCopy(newValues) };
};

export const componentIsRelevantToScreener = (component: Component, workflow: Workflow) => {
	return (
		component.fieldNames.length !== 0 &&
		component.name !== 'Question' &&
		component.name !== 'Paragraph' &&
		component.name !== 'Statement' &&
		component.name !== 'HiddenField' &&
		evalBoolean(component.visible, workflow)
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormattedValue = (workflow: Workflow, component: Component, index: number = null) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let value: any = null;
	const fieldName = component.fieldNames[0];
	if (hasValue(index)) {
		const [arrayFieldName, ...innerFieldName] = fieldName.split('.');
		let correctArrayFieldName = null;
		switch (arrayFieldName) {
			case 'additionalName':
				correctArrayFieldName = 'additionalNames';
				break;
			case 'addressHistory':
				correctArrayFieldName = 'addressHistories';
				break;
			default:
				throw new Error(`Unexpected array field name: ${arrayFieldName}`);
		}
		const array = getNestedFieldValue(workflow, correctArrayFieldName);
		const element = array[index];
		value = getNestedFieldValue(element, innerFieldName.join('.'));
	} else {
		value = getNestedFieldValue(workflow, fieldName);
	}

	let formattedValue: string = value;
	if (hasValue(value)) {
		if (hasValue(component.props.values) && hasValue((component.props.values[0] as Option<string>)?.text)) {
			formattedValue =
				(component.props.values as Option<string>[]).find((x: Option<string>) => x.value === value)?.text ?? value;
		} else if (component.props.type === 'date' || component.props.type === 'monthYear') {
			formattedValue = getFormattedDate(value as string);
		} else if (component.props.type === 'checkbox') {
			formattedValue = value.toString();
		} else if (component.props.type === 'checkList' && Array.isArray(value)) {
			formattedValue = value.join(';') + (value.length > 0 ? ';' : '');
		}
	}
	return formattedValue;
};
